/** Discriminated error kinds so callers can branch without inspecting HTTP codes. */
export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'validation'
  | 'server'
  | 'unknown';

export interface ApiValidationIssue {
  readonly field: string;
  readonly messages: readonly string[];
}

/**
 * Normalized transport error. Every failure leaving the API client is one of these, so
 * feature code never has to reason about raw Axios shapes.
 */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly issues: readonly ApiValidationIssue[];
  /**
   * Machine-readable reason from a `/api/v1` Problem Details body, such as
   * `project_code_taken`. Legacy Core routes never send one.
   */
  readonly code: string | undefined;

  constructor(
    kind: ApiErrorKind,
    message: string,
    options: {
      status?: number;
      issues?: readonly ApiValidationIssue[];
      code?: string;
      cause?: unknown;
    } = {}
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiError';
    this.kind = kind;
    this.status = options.status;
    this.issues = options.issues ?? [];
    this.code = options.code;
  }

  /** True when retrying the same request could plausibly succeed. */
  get isRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || this.kind === 'server';
  }
}

/** ASP.NET Core `ValidationProblemDetails`, returned when `ModelState` is invalid. */
export interface ProblemDetailsResponse {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  readonly errors?: Record<string, readonly string[]>;
  /** Only on `/api/v1` routes. */
  readonly code?: string;
}

/** Shape returned by the Core service's `isCount=true` pagination queries. */
export interface CountResponse {
  readonly count: number;
}
